/**
 * NewNumberInput Component
 *
 * Eine verbesserte Zahlen-Eingabekomponente, die auf ArgonInput basiert und
 * nur Zahlen akzeptiert. Verwendet react-text-mask zur Eingabekontrolle.
 *
 * Features:
 * - Konfigurierbare Anzahl von Ganzzahlen und Dezimalstellen
 * - Automatische Erkennung der lokalen Dezimalnotation aus i18.language
 * - Akzeptiert numerische/dezimale Werte in gültiger Systemnotation (mit .)
 * - Gibt immer eine gültige Zahl/Float zurück, die gespeichert und verarbeitet werden kann
 * - Zeigt dem Benutzer die lokale Dezimalnotation an
 */

import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

// Argon Dashboard 2 PRO MUI components
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonInput from 'components/ArgonInput';
import TooltipWrapper from 'components/TooltipWrapper';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Hilfsfunktionen für die Zahlenformatierung
import { getDecimalSeparator } from 'services/numberFormatting';
import { getGroupSeparator } from 'services/numberFormatting';

// Text-Mask Komponenten
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(() => ({
  formControl: {}
}));

/**
 * Hilfsfunktion zum Entfernen mehrerer Dezimaltrennzeichen
 * Behält nur das erste Dezimaltrennzeichen
 */
const ensureSingleDecimalSeparator = (value, separator) => {
  if (!value) return value;

  const parts = value.split(separator);
  if (parts.length <= 1) return value;

  const integerPart = parts[0];
  const decimalParts = parts.slice(1).join('');

  return integerPart + separator + decimalParts;
};

/**
 * TextMaskCustom Komponente
 * Stellt eine maskierte Eingabe für Zahlen bereit
 */
export function TextMaskCustom(props) {
  const {
    inputRef,
    onEnterPressed,
    error,
    helperText,
    width,
    integerLimit,
    decimalLimit,
    prefix,
    suffix,
    allowNegative,
    ...other
  } = props;

  const { i18n } = useTranslation();

  // Erstellen der Zahlenmaske mit den konfigurierten Eigenschaften
  const numberMask = createNumberMask({
    allowDecimal: true,
    prefix: prefix || '',
    suffix: suffix || '',
    decimalSymbol: getDecimalSeparator(i18n.language),
    thousandsSeparatorSymbol: getGroupSeparator(i18n.language),
    includeThousandsSeparator: true,
    decimalLimit: decimalLimit || 2, // Standard: 2 Dezimalstellen
    integerLimit: integerLimit, // Kein Standardwert für integerLimit
    allowNegative: allowNegative || false,
    allowLeadingZeroes: false,
    requireDecimals: false,
    allowDecimalSymbol: true, // Explizit erlauben des Dezimalsymbols
    preventInvalidInput: true // Verhindert ungültige Eingaben
  });

  // Behandlung von Tastatureingaben
  const handleKeyDown = useCallback(
    (event) => {
      const decimalSeparator = getDecimalSeparator(i18n.language);

      // Behandlung von Dezimaltrennzeichen (Punkt oder Komma)
      if (event.key === '.' || event.key === ',') {
        event.preventDefault();
        event.stopPropagation();

        // Simuliere die Eingabe des korrekten Dezimaltrennzeichens
        const input = event.target;
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const value = input.value;

        // Prüfe, ob bereits ein Dezimaltrennzeichen vorhanden ist
        if (value.includes(decimalSeparator)) {
          return; // Verhindere die Eingabe eines zweiten Dezimaltrennzeichens
        }

        // Füge das korrekte Dezimaltrennzeichen an der Cursorposition ein
        const newValue =
          value.substring(0, start) + decimalSeparator + value.substring(end);

        // Aktualisiere den Wert und setze den Cursor nach dem Dezimaltrennzeichen
        input.value = newValue;
        input.setSelectionRange(start + 1, start + 1);

        // Löse ein Input-Event aus, um die Änderung zu registrieren
        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);
      }

      // Enter-Taste behandeln
      if (event.key === 'Enter' && onEnterPressed) {
        onEnterPressed(event.target.value);
      }
    },
    [i18n.language, onEnterPressed]
  );

  return (
    <MaskedInput
      {...other}
      ref={(ref) => {
        inputRef && inputRef(ref ? ref.inputElement : null);
      }}
      mask={numberMask}
      guide={false} // Deaktivieren der Führung für flexiblere Eingabe
      keepCharPositions={false} // Deaktivieren des Beibehaltens von Zeichenpositionen
      onKeyDown={handleKeyDown}
      error={error}
      helperText={helperText}
      style={{ width: width }}
    />
  );
}

TextMaskCustom.propTypes = {
  inputRef: PropTypes.func.isRequired,
  onEnterPressed: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  integerLimit: PropTypes.number,
  decimalLimit: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  allowNegative: PropTypes.bool
};

/**
 * NewNumberInput Komponente
 * Eine verbesserte Zahlen-Eingabekomponente
 */
function NumberInput({
  id,
  name,
  label,
  value,
  onChange,
  tooltip,
  required,
  integerLimit,
  decimalLimit = 2,
  prefix,
  suffix,
  textColor,
  error,
  helperText,
  size,
  allowNegative = false,
  ...rest
}) {
  const { i18n } = useTranslation();
  const classes = useStyles();

  // Interner State für den angezeigten Wert
  const [displayValue, setDisplayValue] = useState('');

  // Formatieren des Werts für die Anzeige
  const formatNumberForDisplay = (value) => {
    console.log(
      'NumberInput: formatNumberForDisplay: before transformation',
      value
    );
    if (value === undefined || value === null || value === '') {
      return '';
    }

    let localDecimalSeparator = getDecimalSeparator(i18n.language);

    if (localDecimalSeparator === '.') {
      return value;
    }

    if (localDecimalSeparator === ',') {
      return value.toString().replace('.', ',');
    }

    // // Wenn das lokale Dezimaltrennzeichen nicht der Punkt ist
    // if (localDecimalSeparator !== '.') {
    //   // Versuche, den Wert als Zahl zu parsen
    //   const numValue = typeof value === 'string' ? parseFloat(value) : value;

    //   if (!isNaN(numValue)) {
    //     // Formatiere die Zahl mit dem lokalen Dezimaltrennzeichen
    //     const formatted = numValue.toLocaleString(i18n.language, {
    //       minimumFractionDigits: 0,
    //       maximumFractionDigits: decimalLimit || 2,
    //       useGrouping: true
    //     });

    //     // Wenn der Wert Dezimalstellen hat, stellen wir sicher, dass diese auch angezeigt werden
    //     if (numValue % 1 !== 0) {
    //       // Wert hat Dezimalstellen
    //       const parts = numValue.toString().split('.');
    //       if (parts.length > 1) {
    //         const decimalPart = parts[1].substring(0, decimalLimit || 2);
    //         const integerPart = formatted.split(localDecimalSeparator)[0];
    //         return (
    //           (prefix || '') +
    //           integerPart +
    //           localDecimalSeparator +
    //           decimalPart +
    //           (suffix || '')
    //         );
    //       }
    //     }

    //     // Füge Präfix und Suffix hinzu, falls vorhanden
    //     let result = (prefix || '') + formatted + (suffix || '');
    //     console.log(
    //       'NumberInput: formatNumberForDisplay: after transformation',
    //       result
    //     );
    //     return result;
    //   }
    // }

    // let result = (prefix || '') + value + (suffix || '');
    // console.log(
    //   'NumberInput: formatNumberForDisplay: after transformation',
    //   result
    // );
    // return result;
  };

  // Behandlung von Änderungen im Eingabefeld
  const handleChange = (event) => {
    event.preventDefault();

    let inputValue = event.target.value;
    const decimalSeparator = getDecimalSeparator(i18n.language);

    // Entferne Präfix und Suffix für die Verarbeitung
    let processedValue = inputValue;
    if (prefix) processedValue = processedValue.replace(prefix, '');
    if (suffix) processedValue = processedValue.replace(suffix, '');

    // Stelle sicher, dass nur ein Dezimaltrennzeichen vorhanden ist
    processedValue = ensureSingleDecimalSeparator(
      processedValue,
      decimalSeparator
    );

    // Konvertiere in Standard-Dezimalformat (mit Punkt)
    let numericValue = processedValue;

    // Wenn das lokale Dezimaltrennzeichen nicht der Punkt ist
    if (decimalSeparator !== '.') {
      // Tausendertrennzeichen entfernen
      numericValue = processedValue.replace(
        new RegExp('\\' + getGroupSeparator(i18n.language), 'g'),
        ''
      );
      // Lokales Dezimaltrennzeichen durch Punkt ersetzen
      numericValue = numericValue.replace(decimalSeparator, '.');
    }

    // Stelle sicher, dass auch im numerischen Wert nur ein Dezimaltrennzeichen vorhanden ist
    numericValue = ensureSingleDecimalSeparator(numericValue, '.');

    // Prüfen, ob der Wert eine gültige Zahl ist
    const isValidNumber =
      !isNaN(parseFloat(numericValue)) ||
      numericValue === '' ||
      numericValue.endsWith('.');

    if (!isValidNumber) {
      return;
    }

    // Formatiere den angezeigten Wert mit dem korrekten Dezimaltrennzeichen
    let displayVal = processedValue;
    if (prefix) displayVal = prefix + displayVal;
    if (suffix) displayVal = displayVal + suffix;

    // Aktualisiere den internen State für den angezeigten Wert
    setDisplayValue(displayVal);

    // Rufe den onChange-Handler mit dem numerischen Wert auf
    if (onChange) {
      onChange(numericValue);
    }
  };

  // Behandlung der Enter-Taste
  const handleEnterPressed = (value) => {
    // Hier könnte zusätzliche Logik für die Enter-Taste implementiert werden
  };

  // Aktualisiere den angezeigten Wert, wenn sich der externe Wert ändert
  useEffect(() => {
    if (value !== undefined && value !== null) {
      console.log('NumberInput: value to show: ', value);
      setDisplayValue(formatNumberForDisplay(value));
    } else {
      setDisplayValue('');
    }
  }, [value, i18n.language, decimalLimit, prefix, suffix]);

  return (
    <>
      {label && (
        <ArgonBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
          <TooltipWrapper tooltip={tooltip}>
            <ArgonTypography
              component="label"
              variant="caption"
              fontWeight="bold"
              textTransform="capitalize"
              color={textColor}
              {...rest}>
              {label}&nbsp;
              {required && <span style={{ color: 'red' }}>*</span>}
              {tooltip && (
                <ArgonBox ml={1} display="inline-block">
                  <ArgonTypography variant="regular">
                    <InfoOutlinedIcon color="secondary" />
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonTypography>
          </TooltipWrapper>
        </ArgonBox>
      )}

      <ArgonInput
        id={id}
        name={name}
        className={classes.formControl}
        value={displayValue}
        onChange={handleChange}
        onEnterPressed={handleEnterPressed}
        error={error}
        helperText={helperText}
        inputComponent={TextMaskCustom}
        size={size}
        inputProps={{
          integerLimit,
          decimalLimit,
          prefix,
          suffix,
          allowNegative
        }}
        variant="outlined"
        {...rest}
      />
    </>
  );
}

// Typüberprüfung für NewNumberInput
NumberInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  tooltip: PropTypes.string,
  required: PropTypes.bool,
  textColor: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  helperText: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  decimalLimit: PropTypes.number,
  integerLimit: PropTypes.number,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  allowNegative: PropTypes.bool,
  endAdornment: PropTypes.node
};

export default NumberInput;
